const DEFAULT_COVER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='450' viewBox='0 0 800 450'%3E%3Crect fill='%23e2e8f0' width='800' height='450'/%3E%3Ctext x='400' y='230' text-anchor='middle' fill='%2394a3b8' font-family='system-ui' font-size='20'%3EArticle%3C/text%3E%3C/svg%3E";

export const ARTICLE_PREVIEW_SECTION_COUNT = 2;

/**
 * Split body sections for guest / no-interest readers.
 * Ensures at least one block stays behind the paywall when any body content exists.
 */
export function splitArticleSectionsForAccess(navSections, canReadFull) {
  const sections = Array.isArray(navSections) ? navSections : [];

  if (canReadFull) {
    return { previewSections: sections, lockedSections: [], showBodyPaywall: false };
  }

  if (!sections.length) {
    return { previewSections: [], lockedSections: [], showBodyPaywall: true };
  }

  if (sections.length === 1) {
    return { previewSections: [], lockedSections: sections, showBodyPaywall: false };
  }

  const previewLen = Math.min(ARTICLE_PREVIEW_SECTION_COUNT, sections.length - 1);
  return {
    previewSections: sections.slice(0, previewLen),
    lockedSections: sections.slice(previewLen),
    showBodyPaywall: false,
  };
}

export function slugify(value = "") {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function findHeroSection(sections = []) {
  return sections.find((s) => s?.type === "hero") || null;
}

export function getSectionNavLabel(section) {
  if (!section) return "Section";
  const data = section.data || {};
  if (data.title?.trim()) return data.title.trim();
  switch (section.type) {
    case "takeaways":
      return "Key takeaways";
    case "quote":
      return data.author?.trim() ? `Quote — ${data.author}` : "Quote";
    case "faq":
      return "FAQs";
    case "checklist":
      return "Checklist";
    case "rich_text":
      return "Details";
    case "text_section":
      return "Overview";
    default:
      return section.id || "Section";
  }
}

export function buildConsumerArticleSlug(row = {}) {
  const hero = findHeroSection(row?.article?.sections || []);
  const meta = row?.article?.meta || {};
  const seo = row?.article?.seo || {};
  const source =
    hero?.data?.title?.trim() ||
    meta.title?.trim() ||
    seo.meta_title?.trim() ||
    "article";
  return slugify(source) || "article";
}

/** Assign stable, unique route slugs when titles collide (e.g. shared SEO meta_title). */
export function ensureUniqueArticleSlugs(rows = []) {
  if (!rows.length) return rows;

  const baseCounts = rows.reduce((acc, row) => {
    const base = buildConsumerArticleSlug(row);
    acc[base] = (acc[base] || 0) + 1;
    return acc;
  }, {});

  const used = new Set();

  return rows.map((row, index) => {
    const base = buildConsumerArticleSlug(row);
    const provider =
      row?.service_provider_slug ||
      (row?.service_provider_id != null ? String(row.service_provider_id) : null);
    const needsSuffix = (baseCounts[base] || 0) > 1;

    const candidates = [base];
    if (needsSuffix && provider) candidates.push(`${base}--${provider}`);
    if (needsSuffix) candidates.push(`${base}-${index + 1}`);

    let routeSlug = base;
    for (const candidate of candidates) {
      if (!used.has(candidate)) {
        routeSlug = candidate;
        break;
      }
    }

    let n = 2;
    while (used.has(routeSlug)) {
      routeSlug = `${base}-${n}`;
      n += 1;
    }
    used.add(routeSlug);

    return { ...row, _routeSlug: routeSlug };
  });
}

/** True when two normalized list rows are the same article (or `routeArticleSlug` is the current page). */
export function isSameConsumerArticle(candidate, current, routeArticleSlug) {
  if (!candidate || !current) return false;

  if (candidate.raw && current.raw && candidate.raw === current.raw) return true;

  if (
    candidate.slug &&
    current.slug &&
    (candidate.slug === current.slug || slugify(candidate.slug) === slugify(current.slug))
  ) {
    return true;
  }

  if (routeArticleSlug && articleSlugMatches(candidate.raw, routeArticleSlug)) {
    return true;
  }

  const candidateProvider =
    candidate.providerSlug ?? candidate.providerId ?? candidate.raw?.service_provider_slug;
  const currentProvider =
    current.providerSlug ?? current.providerId ?? current.raw?.service_provider_slug;

  if (
    candidate.title &&
    current.title &&
    slugify(candidate.title) === slugify(current.title) &&
    String(candidateProvider ?? "") === String(currentProvider ?? "")
  ) {
    return true;
  }

  return false;
}

export function articleSlugMatches(row, articleSlug) {
  if (!articleSlug) return false;
  const key = decodeURIComponent(String(articleSlug).trim());
  const normalizedKey = slugify(key);

  const routeSlug = row?._routeSlug;
  if (routeSlug && (routeSlug === key || slugify(routeSlug) === normalizedKey)) return true;

  const built = buildConsumerArticleSlug(row);
  if (built === key || built === normalizedKey) return true;

  const legacyWithProvider = row?.service_provider_slug || row?.service_provider_id;
  if (legacyWithProvider && key === `${built}--${legacyWithProvider}`) return true;

  const hero = findHeroSection(row?.article?.sections || []);
  const metaTitle = slugify(row?.article?.meta?.title);
  const heroTitle = slugify(hero?.data?.title);
  if (metaTitle && metaTitle === normalizedKey) return true;
  if (heroTitle && heroTitle === normalizedKey) return true;

  return false;
}

export function normalizeConsumerArticle(row = {}) {
  const sections = Array.isArray(row?.article?.sections) ? row.article.sections : [];
  const hero = findHeroSection(sections);
  const meta = row?.article?.meta || {};
  const seo = row?.article?.seo || {};
  const readMinutes = meta.read_time_minutes ?? meta.read_time ?? null;

  const routeSlug = row._routeSlug || buildConsumerArticleSlug(row);

  return {
    id: routeSlug,
    slug: routeSlug,
    title: meta.title || hero?.data?.title || "Untitled article",
    excerpt: meta.excerpt || seo.meta_description || hero?.data?.subtitle || "",
    type: meta.type || hero?.data?.badge || "Guide",
    status: meta.status || "published",
    readTime: readMinutes ? `${readMinutes} min read` : "5 min read",
    readTimeMinutes: readMinutes,
    coverImage:
      hero?.data?.cover_image?.url ||
      (typeof seo.og_image === "string" ? seo.og_image : "") ||
      row?.cover_image_url ||
      DEFAULT_COVER,
    coverAlt: hero?.data?.cover_image?.alt || meta.title || "",
    badge: hero?.data?.badge || meta.type || "Guide",
    subtitle: hero?.data?.subtitle || meta.excerpt || "",
    sections,
    navSections: sections.filter((s) => s?.type !== "hero"),
    providerId: row?.service_provider_id || null,
    providerSlug: row?.service_provider_slug || null,
    providerName:
      row?.service_provider_name ||
      row?.service_provider?.name ||
      row?.service_provider?.company_name ||
      row?.service_provider?.display_name ||
      row?.provider_name ||
      row?.display_name ||
      null,
    categoryName: row?.category?.name || "",
    subCategoryName: row?.sub_category?.name || "",
    raw: row,
  };
}

export function sanitizeArticleHtml(html = "") {
  if (!html) return "";
  let safe = String(html).replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    "",
  );
  safe = safe.replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  return safe;
}

export const ARTICLE_PROSE_CLASS =
  "prose prose-slate max-w-none text-base leading-8 text-gray-700 prose-headings:font-black prose-headings:text-gray-950 prose-h2:text-2xl prose-h3:text-xl prose-p:my-4 prose-a:text-[#9a7b00] prose-a:font-semibold prose-img:my-6 prose-img:w-full prose-img:max-w-full prose-img:rounded-2xl prose-img:border prose-img:border-black/[0.06] prose-ul:my-4 prose-ol:my-4 prose-li:my-1 prose-blockquote:border-l-4 prose-blockquote:border-[#c9a000] prose-blockquote:pl-4";
